/**
 * /account/insurance — customer-facing insurance directory + claims receipts.
 *
 * Two sections:
 *   1. "Recommended insurance partners" — filtered by customer country.
 *   2. "Generate a claims receipt" — pet picker + date range, fires
 *      POST /api/customer/insurance/claims-receipt.
 *
 * "Already insured? Tell us about your policy" deliberately defers to the
 * existing /account/pets edit flow (already captures insurance fields).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatMoney } from '../lib/format';

interface PartnerRow {
  id: string;
  name: string;
  description: string;
  countryCode: string;
  logoUrl: string | null;
  referralUrl: string;
  supportedSpecies: string[];
  isPlatform: boolean;
}

interface PartnersResponse {
  enabled: boolean;
  country?: string;
  partners: PartnerRow[];
}

interface CustomerPet {
  id: string;
  name: string;
  species: string;
  insuranceProvider: string | null;
  insurancePolicyNumber: string | null;
  insuranceExpiresOn: string | null;
}

interface ReceiptResult {
  receiptId: string;
  fileId: string | null;
  downloadUrl: string | null;
  expiresAt: string | null;
  totalMinor: number;
  lineCount: number;
  filename: string;
}

const CATEGORIES = ['medical', 'boarding', 'grooming', 'daycare', 'other'] as const;

export function InsurancePage() {
  const [partners, setPartners] = useState<PartnersResponse | null>(null);
  const [pets, setPets] = useState<CustomerPet[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([
      fetch('/api/customer/insurance/partners', { credentials: 'include' }).then((r) =>
        r.ok ? (r.json() as Promise<PartnersResponse>) : null,
      ),
      fetch('/api/customer/pets', { credentials: 'include' }).then((r) =>
        r.ok ? (r.json() as Promise<{ pets: CustomerPet[] }>) : { pets: [] },
      ),
    ])
      .then(([p, petList]) => {
        setPartners(p ?? { enabled: false, partners: [] });
        setPets(petList?.pets ?? []);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <p className="p-6 text-sm text-muted-foreground">Loading...</p>;
  }
  if (error) {
    return <p className="p-6 text-sm text-destructive">{error}</p>;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-8 px-4 py-6 sm:px-6 sm:py-10">
      <header>
        <h1 className="text-3xl font-semibold">Pet insurance</h1>
        <p className="mt-2 text-muted-foreground">
          Compare trusted insurance partners and generate claims-friendly receipts.
        </p>
      </header>

      {partners?.enabled && partners.partners.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Recommended partners</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {partners.partners.map((p) => (
              <PartnerCard key={p.id} partner={p} />
            ))}
          </div>
        </section>
      )}

      <section className="space-y-4">
        <h2 className="text-xl font-semibold">Already insured?</h2>
        <p className="text-sm text-muted-foreground">
          Add your policy details on each pet's profile and we'll keep them on file for any future
          claims.
        </p>
        <Link
          to="/account/pets"
          className="inline-block rounded border px-3 py-1.5 text-sm font-medium"
        >
          Edit pet insurance details
        </Link>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold">Generate a claims receipt</h2>
        <p className="text-sm text-muted-foreground">
          Build an itemised receipt for any date range to attach to your insurer's claim form.
        </p>
        <ReceiptForm pets={pets ?? []} />
      </section>
    </div>
  );
}

function PartnerCard({ partner }: { partner: PartnerRow }) {
  return (
    <div className="rounded-lg border p-4 flex flex-col gap-2">
      <div className="flex items-start gap-3">
        {partner.logoUrl ? (
          <img
            src={partner.logoUrl}
            alt={partner.name}
            className="size-12 rounded object-contain"
          />
        ) : (
          <div className="flex size-12 items-center justify-center rounded bg-muted text-lg font-semibold">
            {partner.name.charAt(0)}
          </div>
        )}
        <div className="flex-1">
          <h3 className="font-medium">{partner.name}</h3>
          <p className="text-xs text-muted-foreground">
            Covers: {partner.supportedSpecies.join(', ')}
          </p>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">{partner.description}</p>
      <a
        href={partner.referralUrl}
        target="_blank"
        rel="noreferrer noopener"
        className="mt-auto inline-block rounded bg-primary px-3 py-1.5 text-center text-sm font-medium text-primary-foreground"
      >
        Get a quote
      </a>
    </div>
  );
}

function ReceiptForm({ pets }: { pets: CustomerPet[] }) {
  const [petId, setPetId] = useState<string>(pets[0]?.id ?? '');
  const [fromDate, setFromDate] = useState<string>(() => isoDate(-90));
  const [toDate, setToDate] = useState<string>(() => isoDate(0));
  const [included, setIncluded] = useState<Set<string>>(new Set(CATEGORIES));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ReceiptResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch('/api/customer/insurance/claims-receipt', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          petId,
          fromDate,
          toDate,
          includedCategories: Array.from(included),
        }),
      });
      const body = (await res.json().catch(() => ({}))) as ReceiptResult & {
        error?: string;
        message?: string;
      };
      if (!res.ok) {
        throw new Error(body.message ?? body.error ?? `failed: ${res.status}`);
      }
      setResult(body);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [petId, fromDate, toDate, included]);

  if (pets.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Add a pet first to generate a claims receipt.</p>
    );
  }

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <label className="block text-sm">
        Pet
        <select
          value={petId}
          onChange={(e) => setPetId(e.currentTarget.value)}
          className="mt-1 w-full rounded border px-2 py-1.5"
        >
          {pets.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.species})
            </option>
          ))}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm">
          From
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.currentTarget.value)}
            className="mt-1 w-full rounded border px-2 py-1.5"
          />
        </label>
        <label className="block text-sm">
          To
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.currentTarget.value)}
            className="mt-1 w-full rounded border px-2 py-1.5"
          />
        </label>
      </div>
      <fieldset className="text-sm">
        <legend className="font-medium mb-2">Categories to include</legend>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((cat) => (
            <label
              key={cat}
              className="flex items-center gap-1.5 rounded border px-2 py-1 text-xs capitalize"
            >
              <input
                type="checkbox"
                checked={included.has(cat)}
                onChange={(e) =>
                  setIncluded((s) => {
                    const next = new Set(s);
                    if (e.currentTarget.checked) next.add(cat);
                    else next.delete(cat);
                    return next;
                  })
                }
              />
              {cat}
            </label>
          ))}
        </div>
      </fieldset>
      <button
        type="button"
        onClick={() => void submit()}
        disabled={busy}
        className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
      >
        {busy ? 'Generating...' : 'Generate receipt'}
      </button>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {result && (
        <div className="rounded border bg-success/10 p-3 text-sm">
          <p className="font-medium text-success">Receipt ready</p>
          <p className="text-xs text-muted-foreground">
            <span className="num">{result.lineCount}</span> line item
            {result.lineCount === 1 ? '' : 's'}, total{' '}
            <span className="num">{formatMoney(result.totalMinor)}</span>
          </p>
          {result.downloadUrl ? (
            <a
              href={result.downloadUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-2 inline-block rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
            >
              Download receipt
            </a>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              Receipt logged. Email with download link should arrive shortly.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function isoDate(daysFromToday: number): string {
  const d = new Date(Date.now() + daysFromToday * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}
